import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Palette, Car, Hash, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { getBorderColor, getBorderWidth, getButtonColor } from '../utils/orderPopupUtils'
import searchIcon from '../../../assets/images/search.svg'

interface CarColor {
  id?: string | number
  name?: { ar?: string; en?: string } | string
  hex?: string
}

interface CarModel {
  id?: string | number
  brand?: { name?: { ar?: string; en?: string } | string; logo?: string; en?: string; ar?: string } | string
  name?: string
  logo?: string
}

interface CarPickupComponentsProps {
  carNumber: string
  carColor: string
  carBrand: string
  carColors: CarColor[]
  carModels: CarModel[]
  isLoadingCarData: boolean
  carColorSearchQuery: string
  carTypeSearchQuery: string
  onCarNumberChange: (value: string) => void
  onCarColorChange: (color: string) => void
  onCarBrandChange: (brand: string, logo?: string) => void
  onCarColorSearchChange: (query: string) => void
  onCarTypeSearchChange: (query: string) => void
  isRTL: boolean
  currentLanguage: string
}

/**
 * Car details section component (for main step)
 */
export const CarDetailsSection: React.FC<{
  carNumber: string
  carColor: string
  carBrand: string
  carLogo?: string
  onCarNumberChange: (value: string) => void
  onCarColorTap: () => void
  onCarTypeTap: () => void
  onClearColor?: () => void
  onClearBrand?: () => void
  isRTL: boolean
}> = ({
  carNumber,
  carColor,
  carBrand,
  carLogo,
  onCarNumberChange,
  onCarColorTap,
  onCarTypeTap,
  onClearColor,
  onClearBrand,
  isRTL,
}) => {
  const { t } = useTranslation()

  return (
    <div className="w-full space-y-2">
      {/* Car Color Selection */}
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1.5 block font-['IBM_Plex_Sans_Arabic']">
          {t('cs.car_color_label') || 'لون السيارة'}
        </label>
        <button
          onClick={onCarColorTap}
          className={`
            w-full h-12 px-4 rounded-xl flex items-center justify-between
            border border-gray-300 bg-white hover:border-gray-400 transition-all
            flex-row-reverse
          `}
        >
              <div className={`flex items-center gap-2 flex-row-reverse`}>
            {carColor && onClearColor && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onClearColor()
                }}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            )}
            {!carColor && (isRTL ? <ChevronLeft className="w-5 h-5 text-gray-600" /> : <ChevronRight className="w-5 h-5 text-gray-600" />)}
          </div>
          <span className="text-sm font-medium text-gray-700 font-['IBM_Plex_Sans_Arabic']">
            {carColor || t('cs.choose_car_color_placeholder') || 'اختر لون السيارة'}
          </span>
      
        </button>
      </div>

      {/* Car Type Selection */}
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1.5 block font-['IBM_Plex_Sans_Arabic']">
          {t('cs.car_type_label') || 'نوع السيارة'}
        </label>
        <button
          onClick={onCarTypeTap}
          className={`
            w-full h-12 px-4 rounded-xl flex items-center justify-between
            border border-gray-300 bg-white hover:border-gray-400 transition-all
            flex-row-reverse
          `}
        >
              <div className={`flex items-center gap-2 flex-row-reverse`}>
          {!carBrand && (isRTL ? <ChevronLeft className="w-5 h-5 text-gray-600" /> : <ChevronRight className="w-5 h-5 text-gray-600" />)}

            {carBrand && onClearBrand && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onClearBrand()
                }}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            )}
          </div>
          <div className={`flex items-center gap-2 flex-row-reverse`}>
            {carLogo && (
              <img
                src={carLogo}
                alt={carBrand}
                className="w-6 h-6 object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                }}
              />
            )}
            <span className="text-sm font-medium text-gray-700 font-['IBM_Plex_Sans_Arabic']">
              {carBrand || t('cs.choose_car_type_placeholder') || 'اختر نوع السيارة'}
            </span>
          </div>
      
        </button>
      </div>

      {/* Car Number Input */}
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1.5 block font-['IBM_Plex_Sans_Arabic']">
          {t('cs.car_number_label') || 'رقم السيارة'}
        </label>
        <div className="relative">
          <input
            type="text"
            value={carNumber}
            onChange={(e) => onCarNumberChange(e.target.value)}
            placeholder={t('cs.Enter_the_car_number') || 'أدخل رقم السيارة'}
            className={`
              w-full h-12 px-4 rounded-xl border border-gray-300 bg-white
              text-sm font-medium font-['IBM_Plex_Sans_Arabic']
              focus:outline-none focus:border-[var(--brand-primary)]
              ${isRTL ? 'text-right' : 'text-left'}
            `}
          />
          {carNumber && (
            <button
              onClick={() => onCarNumberChange('')}
              className={`absolute top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors ${isRTL ? 'left-2' : 'right-2'}`}
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Build car details section - wrapper for backward compatibility
 */
export const buildCarDetailsSection = (props: {
  carNumber: string
  carColor: string
  carBrand: string
  carLogo?: string
  onCarNumberChange: (value: string) => void
  onCarColorTap: () => void
  onCarTypeTap: () => void
  onClearColor?: () => void
  onClearBrand?: () => void
  isRTL: boolean
}): React.ReactElement => {
  return <CarDetailsSection {...props} />
}

/**
 * Car color selection step component
 */
export const CarColorStep: React.FC<{
  carColors: CarColor[]
  filteredCarColors: CarColor[]
  isLoadingCarData: boolean
  carColorSearchQuery: string
  selectedCarColor: string
  onColorSelected: (color: string) => void
  onDone: () => void
  onSearchChange: (query: string) => void
  isRTL: boolean
  currentLanguage: string
}> = ({
  carColors,
  filteredCarColors,
  isLoadingCarData,
  carColorSearchQuery,
  selectedCarColor,
  onColorSelected,
  onDone,
  onSearchChange,
  isRTL,
  currentLanguage,
}) => {
  const { t } = useTranslation()

  const getColorDisplayName = (color: CarColor): string => {
    if (typeof color.name === 'string') return color.name
    if (color.name && typeof color.name === 'object') {
      return currentLanguage === 'ar' ? color.name.ar || color.name.en || '' : color.name.en || color.name.ar || ''
    }
    return ''
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Search Bar */}
      <div className="flex-shrink-0 px-4 pt-4 pb-3 sm:pb-4">
        <div className="relative">
          <img
            src={searchIcon}
            alt="search"
            className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${isRTL ? 'right-3' : 'left-3'}`}
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
            }}
          />
          <input
            type="text"
            value={carColorSearchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('cs.searchsimple') || 'بحث'}
            className={`
              w-full min-h-[44px] sm:min-h-[52px] md:min-h-[60px] px-9 sm:px-10 rounded-xl border border-gray-300 bg-gray-50
              text-sm font-medium font-['IBM_Plex_Sans_Arabic']
              focus:outline-none focus:border-[var(--brand-primary)]
              ${isRTL ? 'text-right' : 'text-left'}
            `}
          />
          {carColorSearchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'}`}
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
            </button>
          )}
        </div>
      </div>

      {/* Colors Grid */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-2">
        {isLoadingCarData ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)]"></div>
          </div>
        ) : filteredCarColors.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-sm text-gray-600 font-['IBM_Plex_Sans_Arabic'] text-center px-4">
              {carColorSearchQuery
                ? t('cs.No_colors_match_your_search') || 'لا توجد ألوان تطابق البحث'
                : t('cs.no_colors_available') || 'لا توجد ألوان متاحة'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 pb-2">
            {filteredCarColors.map((color, index) => {
              const colorName = getColorDisplayName(color)
              const colorHex = color.hex || '#000000'
              const isSelected = selectedCarColor?.trim() === colorName?.trim()

              return (
              <button
                key={color.id || index}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onColorSelected(colorName)
                }}
                className={`
                  min-h-[95px] sm:min-h-[105px] md:min-h-[110px] rounded-xl 
                  flex flex-col items-center justify-center gap-2 sm:gap-3 p-2 sm:p-3
                  transition-all cursor-pointer hover:opacity-90
                `}
                style={{
                  border: `${getBorderWidth(isSelected)}px solid ${getBorderColor(isSelected)}`,
                  backgroundColor: '#FFFFFF',
                }}
                type="button"
              >
                {/* Color Circle */}
                <div
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 border-gray-300 flex-shrink-0"
                  style={{ backgroundColor: colorHex }}
                />
                {/* Color Name */}
                <span
                  className="text-[11px] sm:text-[12px] font-semibold font-['IBM_Plex_Sans_Arabic'] text-center line-clamp-2 leading-tight"
                  style={{ color: isSelected ? 'var(--brand-primary)' : '#000000' }}
                >
                  {colorName}
                </span>
              </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Done Button */}
      <div className="flex-shrink-0 px-4 pt-3 sm:pt-4 pb-4">
        <button
          onClick={onDone}
          disabled={!selectedCarColor}
          className={`
            w-full min-h-[56px] sm:min-h-[60px] md:min-h-[65px] rounded-xl
            flex items-center justify-center
            font-semibold text-sm sm:text-base font-['IBM_Plex_Sans_Arabic']
            transition-all
            ${selectedCarColor ? 'hover:opacity-90' : 'cursor-not-allowed opacity-50'}
          `}
          style={{
            backgroundColor: selectedCarColor ? getButtonColor(true) : getButtonColor(false),
            color: selectedCarColor ? '#FFFFFF' : '#9CA3AF',
          }}
        >
          {t('common.done') || 'تم'}
        </button>
      </div>
    </div>
  )
}

/**
 * Build car color selection step - wrapper for backward compatibility
 */
export const buildCarColorStep = (props: {
  carColors: CarColor[]
  filteredCarColors: CarColor[]
  isLoadingCarData: boolean
  carColorSearchQuery: string
  selectedCarColor: string
  onColorSelected: (color: string) => void
  onDone: () => void
  onSearchChange: (query: string) => void
  isRTL: boolean
  currentLanguage: string
}): React.ReactElement => {
  return <CarColorStep {...props} />
}

/**
 * Car type/brand selection step component
 */
export const CarTypeStep: React.FC<{
  carModels: CarModel[]
  filteredCarModels: CarModel[]
  isLoadingCarData: boolean
  carTypeSearchQuery: string
  selectedCarBrand: string
  onBrandSelected: (brand: string, logo?: string, modelName?: string) => void
  onDone: () => void
  onSearchChange: (query: string) => void
  isRTL: boolean
  currentLanguage: string
}> = ({
  carModels,
  filteredCarModels,
  isLoadingCarData,
  carTypeSearchQuery,
  selectedCarBrand,
  onBrandSelected,
  onDone,
  onSearchChange,
  isRTL,
  currentLanguage,
}) => {
  const { t } = useTranslation()

  const getBrandName = (model: CarModel): string => {
    if (typeof model.brand === 'string') return model.brand
    if (model.brand && typeof model.brand === 'object') {
      // API structure: brand: { en: string, ar: string }
      const brandObj = model.brand as { en?: string; ar?: string; name?: { ar?: string; en?: string } | string; logo?: string }
      if ('en' in brandObj || 'ar' in brandObj) {
        return currentLanguage === 'ar' 
          ? (brandObj.ar || brandObj.en || '') 
          : (brandObj.en || brandObj.ar || '')
      }
      // Fallback for other structures
      const brandName = brandObj.name
      if (typeof brandName === 'string') return brandName
      if (brandName && typeof brandName === 'object') {
        return currentLanguage === 'ar' ? brandName.ar || brandName.en || '' : brandName.en || brandName.ar || ''
      }
    }
    return (model as any).name || ''
  }

  const getBrandLogo = (model: CarModel): string | undefined => {
    // API structure: logo is directly on the model object
    if (model.logo && typeof model.logo === 'string') {
      return model.logo
    }
    // Fallback: check if logo is in brand object
    if (model.brand && typeof model.brand === 'object' && 'logo' in model.brand) {
      return (model.brand as any).logo
    }
    return undefined
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Search Bar */}
      <div className="flex-shrink-0 px-4 pt-4 pb-3 sm:pb-4 md:pb-5">
        <div className="relative">
          <img
            src={searchIcon}
            alt="search"
            className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${isRTL ? 'right-3' : 'left-3'}`}
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
            }}
          />
          <input
            type="text"
            value={carTypeSearchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('cs.searchsimple') || 'بحث'}
            className={`
              w-full min-h-[44px] sm:min-h-[48px] px-9 sm:px-10 rounded-xl border border-gray-300 bg-white
              text-sm font-medium font-['IBM_Plex_Sans_Arabic']
              focus:outline-none focus:border-[var(--brand-primary)]
              ${isRTL ? 'text-right' : 'text-left'}
            `}
          />
          {carTypeSearchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'}`}
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
            </button>
          )}
        </div>
      </div>

      {/* Brands Grid */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-2">
        {isLoadingCarData ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)]"></div>
          </div>
        ) : filteredCarModels.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-sm text-gray-600 font-['IBM_Plex_Sans_Arabic'] text-center px-4">
              {carTypeSearchQuery
                ? t('cs.no_search_results') || 'لا توجد نتائج للبحث'
                : t('cs.no_brands_available') || 'لا توجد ماركات متاحة'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 pb-2">
            {filteredCarModels.map((model, index) => {
            const brandName = getBrandName(model)
              const brandLogo = getBrandLogo(model)
              const isSelected = selectedCarBrand?.trim() === brandName?.trim()
            const rawModelName = typeof model.name === 'string' ? model.name.trim() : ''
            const resolvedModelName = rawModelName || (brandName?.trim() || '')

              return (
                <button
                  key={model.id || index}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  onBrandSelected(brandName, brandLogo, resolvedModelName)
                  }}
                className={`
                  min-h-[90px] sm:min-h-[96px] md:min-h-[100px] rounded-xl 
                  flex flex-col items-center justify-center gap-2 sm:gap-3 p-2 sm:p-3
                  transition-all cursor-pointer hover:opacity-90
                `}
                  style={{
                    border: `${getBorderWidth(isSelected)}px solid ${getBorderColor(isSelected)}`,
                    backgroundColor: '#FFFFFF',
                  }}
                  type="button"
                >
                  {/* Brand Logo */}
                  {brandLogo && (
                    <img
                      src={brandLogo}
                      alt={brandName}
                      className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 object-contain flex-shrink-0"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  )}
                  {/* Brand Name */}
                  <span
                    className="text-xs sm:text-sm md:text-[15px] font-semibold font-['IBM_Plex_Sans_Arabic'] text-center line-clamp-2 leading-tight"
                    style={{ color: isSelected ? 'var(--brand-primary)' : '#000000' }}
                  >
                    {brandName}
                  </span>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Done Button */}
      <div className="flex-shrink-0 px-4 pt-3 sm:pt-4 pb-4">
        <button
          onClick={onDone}
          disabled={!selectedCarBrand}
          className={`
            w-full min-h-[56px] sm:min-h-[60px] md:min-h-[64px] rounded-xl
            flex items-center justify-center
            font-semibold text-sm sm:text-base font-['IBM_Plex_Sans_Arabic']
            transition-all
            ${selectedCarBrand ? 'hover:opacity-90' : 'cursor-not-allowed opacity-50'}
          `}
          style={{
            backgroundColor: selectedCarBrand ? getButtonColor(true) : getButtonColor(false),
            color: selectedCarBrand ? '#FFFFFF' : '#9CA3AF',
          }}
        >
          {t('common.done') || 'تم'}
        </button>
      </div>
    </div>
  )
}

/**
 * Build car type/brand selection step - wrapper for backward compatibility
 */
export const buildCarTypeStep = (props: {
  carModels: CarModel[]
  filteredCarModels: CarModel[]
  isLoadingCarData: boolean
  carTypeSearchQuery: string
  selectedCarBrand: string
  onBrandSelected: (brand: string, logo?: string, modelName?: string) => void
  onDone: () => void
  onSearchChange: (query: string) => void
  isRTL: boolean
  currentLanguage: string
}): React.ReactElement => {
  return <CarTypeStep {...props} />
}

